import { Stack } from '@mui/material'
import NoDataBlock from 'components/farm/shared/NoDataBlock'
import { getDaysBetweenDates } from 'components/shared/processes'
import { isDateRangeLongerThanMonths } from 'components/shared/processes/chartDateAggregation'
import { useMemo } from 'react'

import { useAppSelector } from 'store/hooks'
import { CardStandart } from 'ui/CardStandart'

import { DateRestriction } from 'ui/DatePickerInput'
import DateRangePicker from 'ui/DatePickerInput/DateRangePicker'
import Preloader from 'ui/Preloader'
import { TypographyHeader } from 'ui/typography/TypographyHeader'
import { useGetMilkYieldChart } from '../../../../hook/query/useGetMilkYieldChart'
import { MilkYieldChart } from './components/MilkYieldChart/MilkYieldChart'
import { MilkYieldHistory } from './components/MilkYieldHistory/MilkYieldHistory'
import MilkYieldInfo from './components/MilkYieldInfo/MilkYieldInfo'
import { MilkYeildTabs } from './components/MilkYieldTabs/MilkYeildTabs'
import { useGetRangeDate } from './hooks/useGetRangeDate'

interface MilkYeildCardProps {
	selectedBarn: number | null
	setSelectedBarn: (id: number | null) => void
	selectedGroup: number[]
	setSelectedGroup: (ids: number[]) => void
}

function MilkYeildCard({
	selectedBarn,
	setSelectedBarn,
	selectedGroup,
	setSelectedGroup,
}: MilkYeildCardProps) {
	const { dateRange, onChangeCallback } = useGetRangeDate()

	const { data, isLoading } = useGetMilkYieldChart({
		start: dateRange.startDate,
		end: dateRange.endDate,
		barn_id: selectedBarn,
		...(selectedGroup.length > 0 && { group_id: selectedGroup.join(',') }),
	})
	const { groupList } = useAppSelector(state => state.groupsReducer)
	const cowCount = useMemo(() => {
		const groups =
			selectedGroup.length > 0
				? groupList.filter(group => selectedGroup.includes(group.id))
				: groupList
		return groups.reduce((sum, group) => sum + (group.cattles_qty || 0), 0)
	}, [groupList, selectedGroup])
	const daysBetweenDates = getDaysBetweenDates(dateRange)
	const shouldAggregateMonthly = isDateRangeLongerThanMonths(dateRange, 3)
	const isEmptyGraph = data?.graph?.length === 0 || !data?.graph
	return (
		<Stack direction='column' gap='24px'>
			<MilkYeildTabs
				selectedBarn={selectedBarn}
				setSelectedBarn={setSelectedBarn}
				setSelectedGroup={setSelectedGroup}
			/>

			<CardStandart sx={{ padding: '24px' }}>
				<Stack direction='column' gap='24px' minHeight='350px'>
					<TypographyHeader>Надои</TypographyHeader>

					<DateRangePicker
						onChangeUpdateDate={onChangeCallback}
						weekFilter
						monthFilter
						currentRange={'Неделя'}
						startDate={dateRange.startDate}
						endDate={dateRange.endDate}
						restrictionType={DateRestriction.PAST}
					/>
					{isLoading ? (
						<Preloader />
					) : isEmptyGraph ? (
						<NoDataBlock margin={'100px 0'} />
					) : (
						<>
							<MilkYieldInfo
								sellMilk={data?.total_sell_milk_sum}
								rejectedMilk={data?.total_reject_milk_sum}
							/>
							<MilkYieldChart
								graph={data?.graph}
								daysBetweenDates={daysBetweenDates}
								shouldAggregateMonthly={shouldAggregateMonthly}
								cowCount={cowCount}
							/>
							<MilkYieldHistory data={data} />
						</>
					)}
				</Stack>
			</CardStandart>
		</Stack>
	)
}

export default MilkYeildCard
